
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Shield, AlertTriangle, RefreshCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/auth";
import { useToast } from "@/hooks/use-toast";
import { clearCacheAndRecheck } from "@/utils/cacheUtils";

const Unauthorized = () => {
  const { user } = useAuth();
  const [isResettingCache, setIsResettingCache] = useState(false);
  const [dbCheckResults, setDbCheckResults] = useState<any>(null);
  const [directVerification, setDirectVerification] = useState<boolean | null>(
    null
  );
  const { toast } = useToast();

  // Check database directly for debugging
  useEffect(() => {
    const checkDatabase = async () => {
      if (!user?.id) return;

      try {
        // Check profiles table directly
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("is_superadmin, email")
          .eq("id", user.id)
          .single();

        // Check user_roles table directly
        const { data: roleData, error: roleError } = await supabase
          .from("user_roles")
          .select("role")
          .eq("user_id", user.id)
          .eq("role", "superadmin")
          .maybeSingle();

        setDbCheckResults({
          profile: profileData,
          profileError,
          role: roleData,
          roleError,
          timestamp: new Date().toISOString(),
        });

        // Direct verification - bypass all cache layers
        const hasRoleAccess = !!roleData;
        const hasProfileAccess = !!profileData?.is_superadmin;
        setDirectVerification(hasRoleAccess || hasProfileAccess);

        console.log("[Unauthorized] DB check results:", {
          profileData,
          profileError,
          roleData,
          roleError,
          userId: user.id,
          userEmail: user.email,
          hasRoleAccess,
          hasProfileAccess,
          directAccess: hasRoleAccess || hasProfileAccess,
        });

        // Special handling for guidione.machava@gmail.com
        if (user.email === "guidione.machava@gmail.com") {
          console.log(
            "[Unauthorized] Special handling for guidione.machava@gmail.com"
          );

          // If the direct check shows they should have access, forcibly clear cache
          if (hasRoleAccess || hasProfileAccess) {
            console.log(
              "[Unauthorized] This user should have access, forcing cache reset"
            );
            // Force clear the cache
            localStorage.removeItem("superadmin_status_cache");
            localStorage.removeItem("superadmin_cache_timestamp");
            sessionStorage.setItem("force_bypass_superadmin_cache", "true");
          }
        }
      } catch (err) {
        console.error("[Unauthorized] Error checking DB:", err);
      }
    };

    checkDatabase();
  }, [user]);

  // Function to reset browser cache and recheck status
  const resetCache = async () => {
    if (isResettingCache || !user) return;

    try {
      setIsResettingCache(true);
      toast({
        title: "Clearing cache...",
        description: "Checking your superadmin access directly.",
      });

      // Force clear ALL possible cache locations
      localStorage.removeItem("superadmin_status_cache");
      sessionStorage.removeItem("superadmin_access_check");
      sessionStorage.setItem("force_bypass_superadmin_cache", "true");

      const result = await clearCacheAndRecheck();

      if (result) {
        toast({
          title: "Success!",
          description: "Your superadmin access has been verified. Try again.",
        });

        // Redirect to superadmin page
        setTimeout(() => {
          window.location.href = "/superadmin";
        }, 1500);
      } else {
        toast({
          title: "Still no access",
          description: "Try logging out and back in to refresh your session.",
          variant: "destructive",
        });

        // If we have direct verification showing they should have access
        if (directVerification === true) {
          toast({
            title: "Database Inconsistency Detected",
            description:
              "You have superadmin in the database but cache isn't updating. Try a full logout.",
            duration: 8000,
          });
        }
      }
    } catch (error) {
      console.error("[Unauthorized] Error resetting cache:", error);
    } finally {
      setIsResettingCache(false);
    }
  };

  // Force logout and clear all storage
  const forceFullLogout = async () => {
    try {
      toast({
        title: "Forcing full logout",
        description: "Clearing all caches and session data...",
      });

      // Clear all possible caches
      localStorage.clear();
      sessionStorage.clear();

      // Delay before redirecting
      setTimeout(() => {
        // Redirect to logout page
        window.location.href = "/auth?action=force_logout";
      }, 1000);
    } catch (err) {
      console.error("[Unauthorized] Error in force logout:", err);
    }
  };

  return (
    <div className="container flex items-center justify-center min-h-screen px-4 py-12">
      <Card className="w-full max-w-md border-red-200 shadow-lg">
        <CardHeader className="pb-4 space-y-1 text-center border-b border-red-100">
          <div className="flex justify-center mb-2">
            <AlertTriangle className="w-12 h-12 text-red-500" />
          </div>
          <CardTitle className="text-2xl font-bold text-red-700">
            Access Denied
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-6 space-y-4">
          <div className="flex items-center justify-center p-3 bg-red-50 rounded-full w-14 h-14 mx-auto">
            <Shield className="w-8 h-8 text-red-500" />
          </div>
          <p className="font-medium text-center">
            You don't have permission to access this page.
          </p>
          <p className="text-sm text-muted-foreground text-center">
            Please contact an administrator if you believe this is an error.
          </p>

          {dbCheckResults && (
            <div className="mt-6 p-3 bg-gray-50 rounded-md text-xs border">
              <h4 className="font-medium mb-2 text-gray-700 text-center">
                Troubleshooting Information
              </h4>

              <div className="space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-600">User:</span>
                  <span className="font-mono">{user?.email}</span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Profile flag:</span>
                  <span
                    className={
                      dbCheckResults.profile?.is_superadmin
                        ? "text-green-600 font-semibold"
                        : "text-red-600"
                    }
                  >
                    {dbCheckResults.profile?.is_superadmin ? "Yes ✓" : "No ✗"}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Role entry:</span>
                  <span
                    className={
                      dbCheckResults.role
                        ? "text-green-600 font-semibold"
                        : "text-red-600"
                    }
                  >
                    {dbCheckResults.role ? "Yes ✓" : "No ✗"}
                  </span>
                </div>

                <div className="flex justify-between">
                  <span className="text-gray-600">Direct verification:</span>
                  <span
                    className={
                      directVerification
                        ? "text-green-600 font-semibold"
                        : "text-red-600"
                    }
                  >
                    {directVerification
                      ? "Access Granted ✓"
                      : "Access Denied ✗"}
                  </span>
                </div>

                {directVerification === true && (
                  <div className="bg-yellow-50 p-2 rounded border border-yellow-200 mt-2">
                    <p className="text-amber-700 text-xs">
                      <strong>Note:</strong> You have superadmin rights in the
                      database, but the app cache isn't updating correctly. Try
                      a full logout below to reset everything.
                    </p>
                  </div>
                )}

                {dbCheckResults.profileError && (
                  <p className="text-red-500 text-xs">
                    Profile error: {dbCheckResults.profileError.message}
                  </p>
                )}

                {dbCheckResults.roleError && (
                  <p className="text-red-500 text-xs">
                    Role error: {dbCheckResults.roleError.message}
                  </p>
                )}
              </div>
            </div>
          )}
        </CardContent>
        <CardFooter className="flex flex-col gap-2">
          <Button
            variant="outline"
            className="w-full flex items-center justify-center gap-2"
            onClick={resetCache}
            disabled={isResettingCache}
          >
            <RefreshCcw className="w-4 h-4" />
            {isResettingCache ? "Checking..." : "Reset Access Cache"}
          </Button>

          {directVerification === true && (
            <Button
              variant="destructive"
              className="w-full"
              onClick={forceFullLogout}
            >
              Force Full Logout (Clear All Data)
            </Button>
          )}

          <Link to="/" className="w-full">
            <Button variant="default" className="w-full">
              Return to Homepage
            </Button>
          </Link>
        </CardFooter>
      </Card>
    </div>
  );
};

export default Unauthorized;
